import { Modal, ModalBody, ModalFoot, ModalHead } from '@dixa/lib-shared-habla';
import { unwrapResult } from '@reduxjs/toolkit';
import { AsyncStatus, MetaConversation } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { Component } from 'react';

import { AddUserResponse } from 'components/usersModal';

import {
  isNumberValid,
  numberFormatterE164,
} from '../../../../../app/utils/telephonyHelpers';
import SearchInputField from '../searchInputField/index';
import UserItem from '../userItem/index';

import styles from './index.module.scss';

type PropsT = {
  meta: {
    searchUserAsync: AsyncStatus;
  };
  ui: {
    isModalShown: boolean;
  };
  users: Array<User>;
  metaConversation: MetaConversation;
  profileMeta: any;
  profile: any;
  presence: any;
  fromNumberCountryCode: number | null;

  addCallerRequest: (payload: any) => Promise<any>;
  addCallerModalClose: () => void;
  addCallerResetUsers: () => void;
  getUsersByQuery: (payload: any) => void;
  userModalAddUser: (payload: any) => Promise<any>;
};

type StateT = {
  shown: boolean;
  init: boolean;
  searchValue: string;
  isValidNumber: boolean;
};

function tryPhoneNumberFormatting(phoneNumber: string) {
  try {
    return numberFormatterE164({ phoneNumber });
  } catch {
    return phoneNumber;
  }
}

const INITIAL_STATE = {
  shown: false,
  init: true,
  searchValue: '',
  isValidNumber: false,
};

export default class AddCallerModal extends Component<PropsT, StateT> {
  constructor(props: PropsT) {
    super(props);

    this.onAddCallerClick = this.onAddCallerClick.bind(this);
    this.onChangeInput = this.onChangeInput.bind(this);
    this.requestGetUsers = this.requestGetUsers.bind(this);
    this.renderUser = this.renderUser.bind(this);
    this.renderEmpty = this.renderEmpty.bind(this);
    this.renderTitle = this.renderTitle.bind(this);

    this.state = { ...INITIAL_STATE };
  }

  static getDerivedStateFromProps(nextProps: PropsT, prevState: StateT) {
    if (nextProps.ui.isModalShown && !prevState.shown) {
      return { ...INITIAL_STATE, shown: true };
    }
    if (!nextProps.ui.isModalShown && prevState.shown) {
      return { shown: false };
    }

    return null;
  }

  onAddCallerClick(user: User | null) {
    const { addCallerRequest, userModalAddUser, metaConversation } = this.props;
    const { searchValue: phoneNumber } = this.state;

    if (user && user.id) {
      if (user.id !== 'newUser') {
        addCallerRequest({
          userId: user.id,
          csid: metaConversation.conversation.id,
        });
      } else {
        const newUser = {
          phone_number: phoneNumber.replace(/ /g, ''),
          user_type: 'contact',
        };

        userModalAddUser(newUser)
          .then(unwrapResult)
          .then(({ data: newUser }: AddUserResponse) => {
            addCallerRequest({
              userId: newUser.id,
              csid: metaConversation.conversation.id,
            });
          });
      }
    }
  }

  onChangeInput({ target: { value } }: any) {
    const { fromNumberCountryCode } = this.props;

    const withoutSpaces = value.replace(/\s/g, '');
    const prefixedWithoutSpaces = `+${fromNumberCountryCode}${withoutSpaces}`;
    const possiblePhoneNumbers = [withoutSpaces, prefixedWithoutSpaces];
    const phoneNumber = possiblePhoneNumbers.find((phoneNumber: string) =>
      isNumberValid({ phoneNumber }),
    );
    const isValidNumber = phoneNumber !== undefined;
    const searchValue = isValidNumber
      ? tryPhoneNumberFormatting(phoneNumber)
      : value;

    this.setState(
      {
        init: false,
        searchValue,
        isValidNumber,
      },
      () => this.requestGetUsers(searchValue),
    );
  }

  requestGetUsers(search: string) {
    const { addCallerResetUsers, getUsersByQuery } = this.props;

    if (typeof search !== 'string' || search.length === 0) {
      return;
    }

    if (search.trim().length > 0) {
      getUsersByQuery({ search });
    } else {
      this.setState({
        init: true,
        searchValue: '',
        isValidNumber: false,
      });
      addCallerResetUsers();
    }
  }

  renderUser(user: User, index: number) {
    const { profile, metaConversation } = this.props;

    return (
      <UserItem
        key={user.id}
        user={user}
        profile={profile}
        metaConversation={metaConversation}
        onAddCallerClick={this.onAddCallerClick}
      />
    );
  }

  renderEmpty() {
    const { init } = this.state;

    const message = init
      ? 'Enter a number or search for an existing contact by typing in their name or phone number (beginning with the country code, eg. +1...)'
      : 'No user found or phone number not valid';

    return <div className={styles.noResults}>{message}</div>;
  }

  renderTitle() {
    const { metaConversation } = this.props;
    const isCSIDPresent = metaConversation?.conversation?.id !== undefined;

    return isCSIDPresent
      ? `Add caller to #${metaConversation.conversation.id}`
      : 'Add caller';
  }

  render() {
    const { users, addCallerModalClose, ui, fromNumberCountryCode } =
      this.props;
    const { isValidNumber, searchValue } = this.state;

    const userList = [...users];

    if (userList.length === 0 && isValidNumber === true) {
      userList.push({
        id: 'newUser',
        avatar_url: null,
        icon: 'phone',
        email: null,
        phone_number: searchValue,
        name: null,
      } as any);
    }

    return (
      <Modal isOpen={ui.isModalShown} onClose={addCallerModalClose}>
        <ModalHead title={this.renderTitle()} onClose={addCallerModalClose} />
        <ModalBody>
          <div className={styles.body}>
            <div>
              <SearchInputField
                fromNumberCountryCode={fromNumberCountryCode}
                isValidNumber={isValidNumber}
                value={searchValue}
                onChangeInput={this.onChangeInput}
              />
              <div className={styles.results}>
                {userList.length > 0
                  ? userList.map(this.renderUser)
                  : this.renderEmpty()}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFoot cancelOnClick={addCallerModalClose} />
      </Modal>
    );
  }
}
